import { NavigationCubit } from '@breakoutlearning/firebase-repository/cubits/NavigationCubit'
import classNames from 'classnames'
import { ConfirmationDialog } from 'components/dialogs/ConfirmationDialog'
import { BankIcon } from 'components/icons/BankIcon'
import { BookClose } from 'components/icons/BookClose'
import { ChatDots } from 'components/icons/ChatDots'
import { FolderIcon } from 'components/icons/Folder'
import { Forum } from 'components/icons/Forum'
import { GearIcon } from 'components/icons/Gear'
import { GraduationCapIcon } from 'components/icons/GraduationCap'
import { GroupIcon } from 'components/icons/Group'
import { LightBulbIcon } from 'components/icons/LightBulb'
import { QuestionIcon } from 'components/icons/Question'
import { Search } from 'components/icons/Search'
import { Shield } from 'components/icons/Shield'
import { NotificationsButton } from 'components/Notifications/NotificationsButton'
import {
  currentEnvColor,
  inBeta,
  inDevelopment,
  inPreview,
  isProdOrBeta,
} from 'config/environment'
import { AnimatePresence, easeOut, m } from 'framer-motion'
import { useRepository } from 'hooks/auth'
import { useCubitBuilder } from 'hooks/cubits'
import {
  NavigationCubitProvider,
  useNavigationCubit,
} from 'hooks/cubits/navigation'
import { useDialogs } from 'hooks/dialogs'
import { useBreakoutUser } from 'hooks/profile'
import { useRootStore } from 'hooks/rootStore'
import { useSettings } from 'hooks/settings'
import { useTranslationTyped } from 'i18n/i18n'
import { observer } from 'mobx-react-lite'
import { useId, useMemo, useRef, useState } from 'react'
import { BreakoutDrawer } from '../../components/design-system/BreakoutDrawer'
import { BookOpen } from '../../components/icons/BookOpen'
import { Dashboard } from '../../components/icons/Dashboard'
import { GridIcon } from '../../components/icons/Grid'
import { Home } from '../../components/icons/Home'
import { KeyCapIcon } from '../../components/icons/KeyCap'
import { Accessibility } from '../../components/icons/Accessibility'
import { PeopleGroup } from '../../components/icons/PeopleGroup'
import { PeopleMultiple } from '../../components/icons/PeopleMultiple'
import { SignOut } from '../../components/icons/SignOut'
import { ThreeLineHorizontalIcon } from '../../components/icons/ThreeLineHorizontal'
import { SampleClassesDialog } from '../instructor/classes/SampleClassesDialog'
import { SparklesIcon } from 'components/icons/Sparkles'
import { NavItem, NavButtonLi, NavRouteLi, NavLinkLi } from './NavLink'
import { BreakoutTooltip } from 'components/design-system/BreakoutTooltip'

const AdminNavigation = observer(function AdminNavigation() {
  const { tt } = useTranslationTyped()
  const user = useBreakoutUser()

  return (
    <>
      <ul className="flex flex-col gap-1">
        {user.isAuthor && (
          <NavRouteLi
            label={tt.navigation.experiences()}
            to="adminLibrary"
            data-testid="adminLibrary"
            Icon={BookOpen}
          />
        )}
        {(user.isEditor || user.isCorre) && (
          <NavRouteLi
            label={tt.navigation.catalogs()}
            to="adminCatalogs"
            data-testid="adminCatalogs"
            Icon={FolderIcon}
          />
        )}
        {user.isCorre && (
          <NavRouteLi
            label={tt.navigation.admin_users()}
            to="adminUsers"
            data-testid="adminUsers"
            Icon={PeopleGroup}
          />
        )}
        {user.isEditor && (
          <NavRouteLi
            label={tt.navigation.global_prompts()}
            to="adminPrompts"
            data-testid="adminPrompts"
            Icon={SparklesIcon}
          />
        )}
        {user.isCorre && (
          <NavRouteLi
            label={tt.navigation.admin_dashboard()}
            to="adminDashboard"
            data-testid="adminDashboard"
            Icon={Dashboard}
          />
        )}
        {user.hasAccessToRoomInspector && user.isCorre && (
          <NavRouteLi
            label={tt.navigation.admin_inspector()}
            to="adminInspector"
            data-testid="adminInspector"
            Icon={Search}
          />
        )}
        {user.isCorre && (
          <NavRouteLi
            label={tt.navigation.admin_demos()}
            to="adminDemos"
            data-testid="adminDemos"
            Icon={Forum}
          />
        )}
      </ul>
      <hr className="my-2 bg-border-grey" />
      <ul className="flex flex-col gap-1">
        <NavRouteLi
          label={tt.navigation.instructor_classes()}
          to="instructorClasses"
          activeOn="instructorClass, instructorClasses, instructorClassAssignment"
          data-testid="instructorClasses"
          Icon={GraduationCapIcon}
        />
        <NavRouteLi
          label={tt.navigation.instructor_library()}
          to="instructorLibrary"
          activeOn="instructorLibrary, instructorSlideDeck"
          data-testid="instructorLibrary"
          Icon={GridIcon}
        />
        <NavRouteLi
          label={tt.navigation.instructor_assistants()}
          to="instructorAssistants"
          data-testid="instructorAssistants"
          Icon={PeopleMultiple}
        />
        <NavRouteLi
          label={tt.navigation.student_view()}
          to="home"
          activeOn="home, assignment, assignmentWithRoom"
          data-testid="home"
          Icon={BookClose}
        />
      </ul>
    </>
  )
})

function FacultyNavigation() {
  const { tt } = useTranslationTyped()
  const user = useBreakoutUser()

  return (
    <ul className="flex flex-col gap-1">
      <NavRouteLi
        label={tt.navigation.classes()}
        to="home"
        activeOn="home, instructorClass, instructorClasses, instructorClassAssignment"
        data-testid="home"
        Icon={Home}
      />
      <NavRouteLi
        label={tt.navigation.instructor_library()}
        to="instructorLibrary"
        activeOn="instructorLibrary, instructorSlideDeck"
        data-testid="instructorLibrary"
        Icon={GridIcon}
      />
      <NavRouteLi
        label={tt.navigation.student_view()}
        to="instructorStudentView"
        activeOn="instructorStudentView, assignment, assignmentWithRoom"
        data-testid="instructorStudentView"
        Icon={BookClose}
      />
      {user.isInstructor && (
        <NavRouteLi
          label={tt.navigation.instructor_assistants()}
          to="instructorAssistants"
          activeOn="instructorAssistants"
          data-testid="instructorAssistants"
          Icon={PeopleMultiple}
        />
      )}
      {user.externalCanDemo.isLoaded && user.externalCanDemo.value && (
        <NavRouteLi
          label={tt.navigation.external_demos()}
          to="externalDemos"
          activeOn="externalDemos"
          data-testid="externalDemos"
          Icon={Forum}
        />
      )}
    </ul>
  )
}

function StudentNavigation() {
  const { tt } = useTranslationTyped()

  return (
    <ul className="flex flex-col gap-1">
      <NavRouteLi
        label={tt.navigation.home()}
        to="home"
        activeOn="home, assignment , assignmentWithRoom"
        Icon={Home}
      />
      <NotificationsOption />
    </ul>
  )
}

function MobileNavigationDrawer() {
  const { tt } = useTranslationTyped()
  const { helpHovered } = useNavigationCubit()

  return (
    <div className="flex h-full flex-col justify-between">
      <div className="my-5 text-center">
        <img
          src="/assets/images/logo.png"
          tabIndex={0}
          width={48}
          className="inline"
          alt={tt.breakout_learning()}
        />
      </div>
      <div className="h-full w-full overflow-y-auto text-left text-sm">
        {helpHovered && <HelpMenu />}
        {!helpHovered && (
          <>
            <NavigationLinksForRole />
            <hr />
            <NavigationLinksStatic />
          </>
        )}
      </div>
    </div>
  )
}

const MobileTopNavigation = observer(function MobileTopNavigation() {
  const { tt } = useTranslationTyped()
  const { animationsEnabled } = useSettings()
  const repository = useRepository()

  const cubit = useCubitBuilder(
    () =>
      new NavigationCubit({ isMobile: true, animationsEnabled, repository }),
    [animationsEnabled, repository]
  )

  return (
    <NavigationCubitProvider value={cubit}>
      <div
        className={`flex flex-row items-center justify-between border-b border-light-grey-text py-3 md:hidden`}
      >
        <div className="w-[25%] flex-none text-left">
          <span className="mx-5"> </span>
          {/* empty for now, necessary for logo to be in the middle */}
        </div>
        <div className="mx-5 flex-1 text-center">
          <img
            src="/assets/images/logo.png"
            width={48}
            className="inline"
            alt={tt.breakout_learning()}
            tabIndex={0}
          />
        </div>
        <div className="w-[25%] flex-none text-right">
          <button
            aria-hidden
            aria-label={tt.navigation.open_navigation_drawer()}
            onClick={() => cubit.open()}
          >
            <ThreeLineHorizontalIcon className="mx-5 inline" />
          </button>
          <BreakoutDrawer
            open={cubit.isOpen}
            onClose={() => cubit.close()}
            className=""
            placement="right"
          >
            {cubit.mobileDrawerMounted && <MobileNavigationDrawer />}
          </BreakoutDrawer>
        </div>
      </div>
    </NavigationCubitProvider>
  )
})

const BrowserNavigation = observer(function Navigation() {
  const { tt } = useTranslationTyped()
  const { animationsEnabled } = useSettings()
  const repository = useRepository()

  const cubit = useCubitBuilder(
    () =>
      new NavigationCubit({ isMobile: false, animationsEnabled, repository }),
    [animationsEnabled, repository]
  )

  const [isHovered, setIsHovered] = useState(false)
  const navLinksRef = useRef<HTMLDivElement>(null)
  const pendingToggleFocusRef = useRef(false)

  const closedOrOpenKey = cubit.isClosed ? 'closed' : 'open'

  const fadeInOut = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: {
      duration: animationsEnabled ? cubit.animationDuration / 1000 : 0,
      ease: easeOut,
    },
  }

  return (
    <NavigationCubitProvider value={cubit}>
      <m.nav
        aria-label={tt.navigation.main()}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="relative box-border hidden rounded-xl bg-surface md:mb-0 md:flex md:h-full md:w-[4%] md:flex-shrink-0 md:flex-grow-0"
        style={{
          minWidth: cubit.isClosed ? '75px' : '190px',
        }}
        animate={{
          minWidth: cubit.isClosed ? '75px' : '190px',
        }}
        transition={{
          duration: animationsEnabled ? cubit.animationDuration / 1000 : 0,
        }}
      >
        <AnimatePresence>
          <m.div
            key={closedOrOpenKey}
            {...fadeInOut}
            className={classNames(
              'absolute bottom-0 left-0 right-0 top-0 flex h-full w-full flex-col items-center justify-start gap-9 py-9 text-center',
              { 'pl-1 pr-3': cubit.isOpen, 'px-[15.5px]': cubit.isClosed }
            )}
          >
            <BrowserNavigationTop
              isHovered={isHovered}
              pendingToggleFocusRef={pendingToggleFocusRef}
            />
            <div
              ref={navLinksRef}
              tabIndex={-1}
              key={`navigation_links_for_role_${closedOrOpenKey}`}
              className="isolate w-full gap-1 px-[2px] py-1 outline-none md:ml-0 md:flex md:h-full md:flex-col md:overflow-y-auto md:overflow-x-visible"
            >
              <NavigationLinksForRole />
            </div>
            <div
              key={`navigation_links_static_${closedOrOpenKey}`}
              className="flex w-full flex-grow flex-row justify-end gap-1 px-[2px] md:flex-col"
            >
              <hr className="mb-2 bg-border-grey" />
              <NavigationLinksStatic />
            </div>
          </m.div>
        </AnimatePresence>
      </m.nav>
    </NavigationCubitProvider>
  )
})

const HelpMenu = observer(function HelpMenu({
  onClose,
  id,
}: {
  onClose?: () => void
  id?: string
}) {
  const { t, tt } = useTranslationTyped()
  const [isHovered, setIsHovered] = useState(false)
  const user = useBreakoutUser()
  const cubit = useNavigationCubit()
  const { animationsEnabled } = useSettings()
  const { isInstructor, sharedSections } = useBreakoutUser()
  const { showDialog } = useDialogs()
  const { isMobile, helpHovered } = cubit

  const supportURL = useMemo(() => {
    const url = new URL('https://www.breakoutlearning.com/support')

    // for hubspot tracking, only students or instructor strings are supported
    // so everything instructor and up is instructor, else student
    const studentOrInstructor =
      user.isStudent || user.isTA ? 'Student' : 'Instructor'

    url.searchParams.append('student_or_instructor', studentOrInstructor)
    url.searchParams.append('email', user.emailAddress)
    return url.toString()
  }, [user.emailAddress, user.isStudent, user.isTA])

  if (!isHovered && !helpHovered) return null

  return (
    <div
      id={id}
      onMouseEnter={!isMobile ? () => setIsHovered(true) : undefined}
      onMouseLeave={!isMobile ? () => setIsHovered(false) : undefined}
      className={classNames({
        'absolute left-full top-1/2 z-10 -translate-y-1/2 pl-5': !isMobile,
        'animate-fade-in': animationsEnabled && !isMobile,
        'animate-toast-enter': animationsEnabled && isMobile,
      })}
    >
      <div
        className={classNames('rounded-xl', {
          'max-w-[175px] border border-outline-light bg-surface p-2': !isMobile,
          'pl-4': isMobile,
        })}
        onKeyUp={(e) => {
          if (e.key === 'Escape') onClose?.()
        }}
      >
        <ul className="flex flex-col gap-1">
          {sharedSections.models.length > 0 && (
            <NavButtonLi
              alwaysShowLabel
              label={tt.navigation.help_menu.sample_class()}
              Icon={BookClose}
              onClick={() => {
                showDialog(() => (
                  <SampleClassesDialog sampleSections={sharedSections.models} />
                ))
                cubit.setHelpHovered(false)
              }}
            />
          )}
          <NavLinkLi
            alwaysShowLabel
            href={supportURL}
            target="_blank"
            label={t('navigation.help_menu.message_support')}
            Icon={ChatDots}
          />
          <NavLinkLi
            alwaysShowLabel
            href="https://knowledgebase.breakoutlearning.com/"
            target="_blank"
            label={tt.navigation.help_menu.knowledge_base()}
            Icon={BookOpen}
          />
          <NavLinkLi
            alwaysShowLabel
            href="https://www.breakoutlearning.com/legal/privacy-policy"
            target="_blank"
            label={t('navigation.help_menu.privacy_policy')}
            Icon={Shield}
          />
          <NavLinkLi
            alwaysShowLabel
            href="https://www.breakoutlearning.com/legal/accessibility-statement"
            target="_blank"
            label={t('navigation.help_menu.accessibility_statement')}
            Icon={Accessibility}
          />
          {!isInstructor && (
            <NavRouteLi
              alwaysShowLabel
              to={'onboarding'}
              label={t('navigation.help_menu.see_how_it_works')}
              Icon={LightBulbIcon}
            />
          )}
        </ul>
      </div>
    </div>
  )
})

const NavigationLinksForRole = observer(function NavigationLinksForRole() {
  const breakoutUser = useBreakoutUser()
  const { showOrganizationsUI } = useRepository().featureFlags.data
  const { tt } = useTranslationTyped()

  return (
    <>
      {breakoutUser.isInternal && <AdminNavigation />}
      {breakoutUser.isFaculty && <FacultyNavigation />}
      {breakoutUser.isStudent && <StudentNavigation />}
      {showOrganizationsUI &&
        (breakoutUser.isCorre ||
          breakoutUser.organizationAdmins.length > 0) && (
          <ul className="flex flex-col gap-1">
            <NavRouteLi
              label={tt.navigation.organizations()}
              to="organizations"
              data-testid="organizations"
              Icon={BankIcon}
            />
          </ul>
        )}
    </>
  )
})

const NotificationsOption = observer(function NotificationsOption({
  hideIfEmpty,
}: {
  hideIfEmpty?: boolean
}) {
  const user = useBreakoutUser()

  const notificationCount = user.notifications.unreadCount

  if (hideIfEmpty && !notificationCount) return null

  return <NotificationsButton className="w-full" />
})

const NavigationLinksStatic = observer(function NavigationLinksStatic() {
  const { isAdmin } = useBreakoutUser()
  const { tt } = useTranslationTyped()
  const { showDialog } = useDialogs()
  const rootStore = useRootStore()
  const cubit = useNavigationCubit()
  const helpButtonRef = useRef<HTMLButtonElement>(null)
  const helpMenuId = useId()

  return (
    <>
      <ul className="flex flex-col gap-1">
        {isAdmin && <NotificationsOption hideIfEmpty />}
        {!isProdOrBeta() && isAdmin && (
          <NavRouteLi
            label={tt.internal.design_system()}
            to="designSystem"
            Icon={KeyCapIcon}
          />
        )}
        <li>
          <div
            onMouseEnter={
              !cubit.isMobile ? () => cubit.setHelpHovered(true) : undefined
            }
            onMouseLeave={
              !cubit.isMobile ? () => cubit.setHelpHovered(false) : undefined
            }
            className={classNames('relative flex rounded-lg [&>*]:grow', {
              'flex-col': cubit.isMobile,
            })}
          >
            <NavItem
              ref={helpButtonRef}
              label={tt.navigation.help()}
              Icon={QuestionIcon}
              aria-expanded={cubit.helpHovered}
              aria-controls={helpMenuId}
              aria-haspopup="true"
              onClick={(e) => {
                e.stopPropagation()
                // Mobile: toggle (no hover available). Desktop: only open
                // (hover may have already opened it; closing is via mouseleave or Escape)
                if (cubit.isMobile) {
                  cubit.setHelpHovered(!cubit.helpHovered)
                } else {
                  cubit.setHelpHovered(true)
                }
              }}
              onKeyDown={(e) => {
                // Prevent page scroll on Space for button-role element
                if (e.key === ' ') e.preventDefault()
              }}
              onKeyUp={(e) => {
                if (e.key === ' ') {
                  cubit.setHelpHovered(!cubit.helpHovered)
                }
                if (e.key === 'Escape') {
                  cubit.setHelpHovered(false)
                }
              }}
            />
            <HelpMenu
              id={helpMenuId}
              onClose={() => {
                cubit.setHelpHovered(false)
                helpButtonRef.current?.focus()
              }}
            />
          </div>
        </li>
        <NavRouteLi
          data-testid="settings"
          to="settings"
          label={tt.navigation.profile()}
          Icon={GearIcon}
        />
        <NavButtonLi
          label={tt.navigation.logout()}
          data-testid="logOut"
          Icon={SignOut}
          onClick={() =>
            showDialog(() => (
              <ConfirmationDialog
                btnTestId="logout-confirmation"
                title={tt.navigation.logout_confirmation()}
                subtitle={tt.navigation.logout_confirmation_subtitle()}
                buttonText={tt.navigation.logout_confirmation_button()}
                onConfirm={async () => {
                  rootStore.navigateTo('logout')
                }}
              />
            ))
          }
        />
        <li>
          <LtiSourceButton />
        </li>
      </ul>
      <EnvironmentBadge />
    </>
  )
})

const LtiSourceButton = observer(function LtiSourceButton() {
  const user = useBreakoutUser()
  const { tt } = useTranslationTyped()
  const ltiSource = user.ltiSource

  if (ltiSource === 'canvas') {
    return (
      <BreakoutTooltip
        treatReactElementAsString
        placement="right"
        content={
          <div className="whitespace-pre-line text-left">
            {tt.navigation.lti_canvas_tooltip()}
          </div>
        }
      >
        <a
          href={`https://${user.ltiDomain}`}
          className="flex h-11 w-11 cursor-pointer flex-row items-center justify-center rounded-lg bg-on-surface-disabled hover:bg-on-surface-var"
        >
          <img
            src="/assets/images/lti/source-canvas.png"
            height={20}
            data-testid="logo"
            tabIndex={0}
            alt={tt.navigation.canvas()}
            className="object-contain"
          />
        </a>
      </BreakoutTooltip>
    )
  }

  if (ltiSource === 'brightspace') {
    return (
      <BreakoutTooltip
        treatReactElementAsString
        placement="right"
        content={
          <div className="whitespace-pre-line text-left">
            {tt.navigation.lti_brightspace_tooltip()}
          </div>
        }
      >
        <a
          href={`https://${user.ltiDomain}`}
          className="flex h-11 w-11 cursor-pointer flex-row items-center justify-center rounded-lg bg-on-surface-disabled hover:bg-on-surface-var"
        >
          <img
            src="/assets/images/lti/source-brightspace.png"
            height={20}
            data-testid="logo"
            tabIndex={0}
            alt={tt.navigation.brightspace()}
            className="object-contain"
          />
        </a>
      </BreakoutTooltip>
    )
  }

  return null
})

const BrowserNavigationTop = observer(function BrowserNavigationTop({
  isHovered,
  pendingToggleFocusRef,
}: {
  isHovered: boolean
  pendingToggleFocusRef: React.MutableRefObject<boolean>
}) {
  const { animationsEnabled } = useSettings()
  const cubit = useNavigationCubit()
  const {
    isOpen,
    isMobile,
    isOpenAnimationInProgress,
    isCloseAnimationInProgress,
  } = cubit
  const { tt } = useTranslationTyped()

  // if isOpen changes in desktop view with animations enabled
  // do NOT re-render the component w/ or w/o label
  // if this updates during the component lifecycle it is on the way out
  // and a new one will render in its place
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isOpenOnMount = useMemo(() => isOpen, [])
  const isOpenToUse = animationsEnabled && !isMobile ? isOpenOnMount : isOpen

  // Callback ref: when the toggle button mounts after a toggle, focus it
  const toggleButtonRefCallback = (node: HTMLButtonElement | null) => {
    if (node && pendingToggleFocusRef.current) {
      pendingToggleFocusRef.current = false
      node.focus()
    }
  }

  return (
    <div
      className={classNames(
        'group h-[51px] min-h-[51px] w-full text-center md:ml-0',
        {
          'flex flex-row justify-between pl-1.5': isOpenToUse,
          inline: !isOpenToUse,
        }
      )}
    >
      {!isOpenToUse && (
        <m.button
          layoutId="nav-toggle"
          ref={toggleButtonRefCallback}
          aria-label={tt.navigation.open_close_navigation()}
          data-testid="open-close-browser-navigation"
          className="mx-auto w-[44px] flex-none rounded-lg text-center hover:bg-core-tertiary focus:bg-core-tertiary"
          transition={{
            layout: {
              duration: animationsEnabled ? cubit.animationDuration / 1000 : 0,
            },
          }}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            pendingToggleFocusRef.current = true
            cubit.toggleCollapsed()
          }}
        >
          <img
            aria-hidden
            src="/assets/images/logo.png"
            width={44}
            // if we are in process of opening, don't show the logo
            // as they must have triggered hover state to toggle the button
            // this prevents odd visual artifacts when hover toggles on/off during animation
            className={classNames('group-focus-within:hidden', {
              hidden:
                (isHovered || isOpenAnimationInProgress) &&
                !isCloseAnimationInProgress,
            })}
            data-testid="logo"
            alt={tt.breakout_learning()}
          />
          <div
            aria-hidden
            className={classNames(
              'flex h-[44px] w-[44px] cursor-pointer items-center justify-center rounded-lg hover:bg-core-tertiary',
              {
                'hidden group-focus-within:!flex':
                  !isHovered ||
                  isOpenAnimationInProgress ||
                  isCloseAnimationInProgress,
              }
            )}
          >
            <GroupIcon strokeWidth={1.25} size={20} />
          </div>
        </m.button>
      )}
      {isOpenToUse && (
        <>
          <img
            src="/assets/images/logo_large.png"
            width={100}
            data-testid="logo"
            alt={tt.breakout_learning()}
            className="object-contain"
          />
          <div
            className={classNames(
              'flex min-h-[50px] w-full items-center justify-center [&>*]:grow',
              {
                invisible:
                  (!isHovered && !isOpenToUse) || isCloseAnimationInProgress,
                'max-w-[30px]': isOpenToUse,
              }
            )}
          >
            <m.button
              layoutId="nav-toggle"
              ref={toggleButtonRefCallback}
              className="cursor-pointer rounded-lg p-2 text-center hover:bg-core-tertiary focus:bg-core-tertiary"
              data-testid="open-close-browser-navigation"
              aria-label={tt.navigation.open_close_navigation()}
              transition={{
                layout: {
                  duration: animationsEnabled
                    ? cubit.animationDuration / 1000
                    : 0,
                },
              }}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                pendingToggleFocusRef.current = true
                cubit.toggleCollapsed()
              }}
            >
              <GroupIcon strokeWidth={1.25} size={20} />
            </m.button>
          </div>
        </>
      )}
    </div>
  )
})

const EnvironmentBadge = () => {
  const backgroundColor = currentEnvColor()

  const text = useMemo(() => {
    if (inDevelopment()) return 'Dev Environment'
    if (inBeta()) return 'Beta Environment'
    if (inPreview()) return 'Test Environment'

    return ''
  }, [])

  const { isOpen } = useNavigationCubit()

  if (!isOpen || !backgroundColor || !text) return null

  return (
    <div className="flex w-full flex-row justify-center">
      <div
        className={classNames(
          'mt-2 w-min text-nowrap rounded-lg px-2 py-1 text-label-small',
          {
            'text-core-on-primary': inDevelopment(),
          }
        )}
        style={{ backgroundColor }}
      >
        {text}
      </div>
    </div>
  )
}

export function Navigation() {
  return (
    <>
      <MobileTopNavigation />
      <BrowserNavigation />
    </>
  )
}
